import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useNavigationStore } from '@/stores/navigation'
import { WHITE_PAPERS, formatDate, getFeaturedWhitePapers, type Topic, type WhitePaper } from '@/data/whitePapers'
import { getValidUserAccessData, saveUserAccessData, isUserAccessValid, getRemainingAccessTime } from '@/utils/userAccess'

// Map of white paper IDs to their actual component routes
const WHITE_PAPER_ROUTES: Record<string, string> = {
  'wp-009': '/whitepapers/ai-in-healthcare-operations',
  'wp-010': '/whitepapers/scheduling-cx',
  'wp-011': '/whitepapers/referral-cx',
  'wp-012': '/whitepapers/probation-cx',
  'wp-013': '/whitepapers/integrated-care',
  'wp-014': '/whitepapers/cell-cx',
}

const TOPICS: Topic[] = [
  "Referrals",
  "Scheduling",
  "eConsult",
  "Case Management",
  "Cell Therapy",
  "Public Health",
  "Probation/Correctional Health",
  "AI & Automation",
  "Interoperability",
]

const WhitePapers: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const navigationStore = useNavigationStore()
  const [query, setQuery] = useState("")
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [pendingPaper, setPendingPaper] = useState<WhitePaper | null>(null)

  // Access form state
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [org, setOrg] = useState("")
  const [phone, setPhone] = useState("") // optional
  const [submitted, setSubmitted] = useState(false)
  const [hasValidAccess, setHasValidAccess] = useState(false)
  const [remainingTime, setRemainingTime] = useState<number>(0)

  const featured = useMemo(() => getFeaturedWhitePapers()[0] || WHITE_PAPERS[0], [])

  // Check for valid user access on component mount
  useEffect(() => {
    const validAccess = isUserAccessValid()
    setHasValidAccess(validAccess)
    
    if (validAccess) {
      const userData = getValidUserAccessData()
      if (userData) {
        setName(userData.name)
        setEmail(userData.email)
        setOrg(userData.organization)
        setPhone(userData.phone || "")
      }
      
      // Set remaining time
      const remaining = getRemainingAccessTime()
      setRemainingTime(remaining)
    }
  }, [])

  const visiblePapers = useMemo(() => {
    const q = query.toLowerCase()
    return WHITE_PAPERS.filter((p) => {
      const matchesQuery =
        !q || p.title.toLowerCase().includes(q) || p.subtitle.toLowerCase().includes(q)
      const matchesTopics =
        selectedTopics.length === 0 || selectedTopics.every((t) => p.topics.includes(t))
      return matchesQuery && matchesTopics
    }).sort((a, b) => (a.date < b.date ? 1 : -1))
  }, [query, selectedTopics])

  function toggleTopic(t: Topic) {
    setSelectedTopics((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))
  }

  function openAccess(paper: WhitePaper) {
    setPendingPaper(paper)
    
    // Check if user has valid access
    if (hasValidAccess) {
      // User has valid access, navigate directly to the white paper
      handleViewInBrowser(paper)
    } else {
      // User needs to fill out form
      setFormOpen(true)
      setSubmitted(false)
    }
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !email || !org) return
    
    // Save user data to cookie
    saveUserAccessData({
      name,
      email,
      organization: org,
      phone: phone || undefined
    })
    
    setSubmitted(true)
    setHasValidAccess(true)
    setRemainingTime(10) // Set to full 10 hours after new submission
    
    // Close modal and navigate directly to the white paper
    setFormOpen(false)
    if (pendingPaper) {
      handleViewInBrowser(pendingPaper)
    }
  }

  function handleBookDemo() {
    navigationStore.openDemoModal()
  }

  function handleViewInBrowser(paper: WhitePaper) {
    const route = WHITE_PAPER_ROUTES[paper.id]
    if (route) {
      navigate(route)
    } else {
      // Fallback to generic route for papers without specific components
      navigate(`/whitepapers/${paper.id}`)
    }
  }

  function handleDownloadPdf() {
    if (!pendingPaper) return
    
    // Check if PDF is available
    if (pendingPaper.pdfUrl === '#' || pendingPaper.pdfUrl === '') {
      alert('PDF download is not yet available for this white paper. Please check back later.')
      return
    }
    
    // Open PDF in a new tab instead of downloading
    window.open(pendingPaper.pdfUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="w-full bg-white text-slate-900">
      {/* HERO */}
      <header
        className="w-full relative"
        style={{ background: "linear-gradient(90deg, #0F2027 0%, #203A43 50%, #2C5364 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-white relative z-10">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
              <span>Eceptionist</span>
              <span className="font-bold text-[#7AC943]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
            </h2>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              {t('whitePapers.hero.title')}
          </h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95 mb-6">
              {t('whitePapers.hero.subtitle')}
            </p>
            <div className="max-w-xl">
              <div className="relative">
                <input
                  className="w-full rounded-lg px-4 py-3 bg-white text-gray-900 placeholder:text-gray-500 shadow-sm"
                  placeholder={t('whitePapers.hero.searchPlaceholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none"
          aria-hidden
        >
          <div className="w-full h-full bg-[url('/textures/pattern.svg')]" />
        </div>
      </header>

      {/* CONTENT */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar Filters */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-32 space-y-4">
            <div className="rounded-2xl border border-slate-200 p-4 bg-white/80 shadow-sm">
              <div className="mb-2 text-sm font-semibold">{t('whitePapers.topics.title')}</div>
              <div className="grid grid-cols-1 gap-2">
                {TOPICS.map((t) => (
                  <label key={t} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={selectedTopics.includes(t)}
                      onChange={() => toggleTopic(t)}
                    />
                    <span className="text-sm">{t}</span>
                  </label>
                ))}
              </div>
              {selectedTopics.length > 0 && (
                <button
                  className="mt-3 text-xs text-slate-600 underline"
                  onClick={() => setSelectedTopics([])}
                >
                  {t('whitePapers.topics.clearTopics')}
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main Column */}
        <div className="lg:col-span-9 space-y-8">
          {/* Featured */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="font-medium text-indigo-700">{t('whitePapers.featured.title')}</div>
                <div className="text-slate-500">{t('whitePapers.featured.updated')} {formatDate(featured.date)}</div>
              </div>
              <h3 className="mt-2 text-2xl font-semibold leading-tight">{featured.title}</h3>
              <p className="text-slate-700 mt-1">{featured.subtitle}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {featured.topics.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-slate-100 text-slate-700 text-xs px-3 py-1"
                  >
                    {t}
                  </span>
                ))}
                <div className="ml-auto flex gap-2">
                  <button
                    className="rounded-lg bg-slate-200 text-slate-800 px-4 py-2 hover:bg-slate-300"
                    onClick={() => openAccess(featured)}
                  >
                    {t('whitePapers.paper.view')}
                  </button>
                  <button
                    className="rounded-lg bg-slate-200 text-slate-800 px-4 py-2 hover:bg-slate-300"
                    onClick={() => openAccess(featured)}
                  >
                    {t('whitePapers.paper.download')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results header */}
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="text-slate-600">
              {visiblePapers.length} {visiblePapers.length === 1 ? t('whitePapers.results.found') : t('whitePapers.results.foundPlural')}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedTopics.map((t) => (
                <span
                  key={`sel-${t}`}
                  className="rounded-full bg-indigo-600/10 text-indigo-700 text-xs px-3 py-1"
                >
                  {t}
                </span>
              ))}
            </div>
        </div>
        
          {/* Grid */}
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {visiblePapers.map((p) => (
               <div
                 key={p.id}
                 className="rounded-2xl border border-slate-200 bg-white/80 shadow-sm flex flex-col"
               >
                 <div className="p-6 flex-1 flex flex-col">
                   <div className="flex items-center justify-between text-xs text-slate-500">
                     <span>{formatDate(p.date)}</span>
                   </div>
                   <h4 className="text-lg font-semibold mt-1 leading-snug">{p.title}</h4>
                   <p className="text-sm text-slate-700 mt-2 line-clamp-3">{p.subtitle}</p>
                   <div className="mt-3 flex flex-wrap gap-2">
                     {p.topics.slice(0, 3).map((t) => (
                       <span
                         key={`${p.id}-${t}`}
                         className="rounded-full bg-slate-100 text-slate-700 text-xs px-2 py-0.5"
                       >
                         {t}
                       </span>
                     ))}
                   </div>
                   <div className="mt-auto pt-4 flex items-stretch gap-3">
                     <button
                       className="flex-1 rounded-md bg-slate-200 text-slate-800 px-4 py-2 hover:bg-slate-300 text-center min-h-[40px] flex items-center justify-center"
                       onClick={() => openAccess(p)}
                     >
                       {t('whitePapers.paper.view')}
                     </button>
                     <button
                       className="flex-1 rounded-md bg-slate-200 text-slate-800 px-4 py-2 hover:bg-slate-300 text-center min-h-[40px] flex items-center justify-center"
                       onClick={() => openAccess(p)}
                     >
                       {t('whitePapers.paper.download')}
                     </button>
                   </div>
                 </div>
               </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA footer */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
          <div className="bg-gradient-to-r from-indigo-600 via-sky-600 to-cyan-500 p-6 sm:p-8">
            <h3 className="text-white text-2xl font-semibold">{t('whitePapers.cta.title')}</h3>
            <p className="text-white/90 mt-1">
              {t('whitePapers.cta.subtitle')}
            </p>
            <div className="mt-4">
              <button
                className="inline-block rounded-lg bg-white text-slate-900 hover:bg-white/90 px-4 py-2 font-medium"
                onClick={handleBookDemo}
              >
                {t('common.bookDemo')}
              </button>
        </div>
      </div>
        </div>
      </section>

      {/* ACCESS MODAL */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold mb-2 text-slate-900">{t('whitePapers.paper.accessTitle')}</h2>
            <p className="text-slate-600 mb-6">
              {hasValidAccess 
                ? `Welcome back! You have access to ${pendingPaper?.title}. Your session expires in ${Math.round(remainingTime)} hours.`
                : `${t('whitePapers.paper.accessDescription')} ${pendingPaper?.title}.`
              }
            </p>
            {!submitted && !hasValidAccess ? (
              <form className="flex flex-col gap-4" onSubmit={submitForm}>
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  placeholder={t('whitePapers.form.name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  type="email"
                  placeholder={t('whitePapers.form.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  placeholder={t('whitePapers.form.organization')}
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                />
                <input
                  className="w-full rounded-lg border px-3 py-2"
                  type="tel"
                  placeholder={t('whitePapers.form.phone')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <div className="flex justify-end gap-3 mt-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2"
                    onClick={() => setFormOpen(false)}
                  >
                    {t('whitePapers.form.cancel')}
                  </button>
                  <button type="submit" className="rounded bg-indigo-600 text-white px-4 py-2">
                    {t('whitePapers.form.access')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-green-800 text-sm">
                  {t('whitePapers.form.success')}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button 
                    className="rounded bg-slate-200 text-slate-800 px-4 py-2 hover:bg-slate-300"
                    onClick={handleDownloadPdf}
                  >
                    {t('whitePapers.form.downloadPdf')}
                  </button>
                  <button 
                    className="rounded border px-4 py-2"
                    onClick={() => {
                      setFormOpen(false)
                      if (pendingPaper) {
                        handleViewInBrowser(pendingPaper)
                      }
                    }}
                  >
                    {t('whitePapers.form.viewInBrowser')}
                  </button>
                </div>
                <button
                  className="mt-4 text-slate-500 hover:text-slate-700 text-sm"
                  onClick={() => setFormOpen(false)}
                >
                  {t('whitePapers.form.close')}
                </button>
              </div>
            )}
        </div>
      </div>
      )}
    </div>
  )
}

export default WhitePapers


